import { css } from '@emotion/react';
import {
  faCog, faCopy, faEllipsisV, faPresentation, faShare, faUsersMedical,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useState,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { useTranslation } from '~/_shared/utils/hooks';

const alignLeftStyle = css({
  textAlign: 'left',
});

type MapRowActionsProps = Readonly<{
  isSnapshot: boolean;
  isShareActive: boolean;
  isOwner: boolean;

  onSettingsClick?: () => void;
  onMapShareSettingsClick?: () => void;
  onTeamShareMapClick: () => void;
  onMapCopyClick?: () => void;
  onOpenInShareModeClick: () => void;
  onRemoveMyselfFromMapClick?: () => void;
}>;

export const MapRowActionsComponent: FC<MapRowActionsProps> = ({
  isOwner,
  isShareActive,
  isSnapshot,
  onMapCopyClick,
  onMapShareSettingsClick,
  onOpenInShareModeClick,
  onSettingsClick,
  onTeamShareMapClick,
  onRemoveMyselfFromMapClick,
  ...restProps
}) => {
  const [t] = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <ContextMenuComponent
      isVisible={isMenuOpen}
      onHide={() => setIsMenuOpen(false)}
      renderMenuContent={() => (
        <>
          {!!onSettingsClick && (
            <ContextMenuItemCoreComponent
              itemStyle={alignLeftStyle}
              data-testid="map-listing-control-menu-settings"
            >
              <ContextMenuDefaultItem
                icon={faCog}
                size="s"
                onClick={onSettingsClick}
              >
                {t('Settings')}
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          )}

          {isShareActive && onMapShareSettingsClick && (
            <ContextMenuItemCoreComponent
              itemStyle={alignLeftStyle}
              data-testid="map-listing-control-menu-share"
            >
              <ContextMenuDefaultItem
                icon={faShare}
                size="s"
                onClick={onMapShareSettingsClick}
              >
                {t('Share')}
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          )}

          {isOwner && !isSnapshot && isShareActive && (
            <ContextMenuItemCoreComponent
              itemStyle={alignLeftStyle}
              data-testid="map-listing-control-menu-team-share"
            >
              <ContextMenuDefaultItem
                icon={faUsersMedical}
                size="s"
                onClick={onTeamShareMapClick}
              >
                {t('Team Map Share')}
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          )}

          {!isOwner && !isSnapshot && onRemoveMyselfFromMapClick && (
            <ContextMenuItemCoreComponent
              itemStyle={alignLeftStyle}
            >
              <ContextMenuDefaultItem
                icon={faShare}
                size="s"
                onClick={onRemoveMyselfFromMapClick}
              >
                {t('mapActions.removeAccess.myself')}
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          )}

          <ContextMenuItemCoreComponent
            itemStyle={alignLeftStyle}
            data-testid="map-listing-control-menu-public"
          >
            <ContextMenuDefaultItem
              icon={faPresentation}
              itemStyle={css({ textAlign: 'left' })}
              size="s"
              onClick={onOpenInShareModeClick}
            >
              {t('Open in Presentation/Share Mode')}
            </ContextMenuDefaultItem>
          </ContextMenuItemCoreComponent>

          {!isSnapshot && !!onMapCopyClick && (
            <ContextMenuItemCoreComponent
              itemStyle={alignLeftStyle}
              data-testid="map-listing-control-menu-copy"
            >
              <ContextMenuDefaultItem
                icon={faCopy}
                size="s"
                onClick={onMapCopyClick}
              >
                {t('Copy Map')}
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          )}
        </>
      )}
    >
      <RoundButtonComponent
        tooltipDelayShow={500}
        tooltipLabel={t('Map Control')}
        buttonStyle={isMenuOpen ? RoundButtonStyle.Quaternary : RoundButtonStyle.SecondaryInverted}
        icon={faEllipsisV}
        size={RoundButtonSize.Small}
        onClick={() => {
          setIsMenuOpen(true);
        }}
        {...restProps}
      />
    </ContextMenuComponent>
  );
};

MapRowActionsComponent.displayName = 'MapRowActionsComponent';
